import { supabase } from './supabase';
import { BRAND, applyBrandTheme } from '../config/brand';

// Datos de mi organización + si soy administrador de la plataforma
export const getMyOrg = async () => {
  const { data, error } = await supabase.rpc('my_org');
  if (error) throw error;
  return data;
};

// El nombre de la organización reemplaza al de las variables de entorno (recibos, reportes)
export const applyOrgBrand = (org) => {
  if (org?.name) BRAND.orgName = org.name;
  BRAND.orgLogo = org?.logo_url || '';
  BRAND.hideProduct = !!org?.hide_product_brand;
  applyBrandTheme(org?.brand_color);
};

export const updateMyOrg = async ({ name, logoUrl, senderName, timezone }) => {
  const { error } = await supabase.rpc('update_my_org', {
    p_name: name, p_logo_url: logoUrl || null, p_sender_name: senderName || null, p_timezone: timezone || null,
  });
  if (error) throw error;
};

// Reduce la imagen a 256px y la sube a la carpeta de la organización (org-logos/<org_id>/logo.png)
export const uploadOrgLogo = async (orgId, file) => {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) throw new Error('Usá una imagen PNG, JPG o WEBP');
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, 256 / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
  c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
  const blob = await new Promise((r) => c.toBlob(r, 'image/png'));
  const path = `${orgId}/logo.png`;
  const { error } = await supabase.storage.from('org-logos').upload(path, blob, { upsert: true, contentType: 'image/png', cacheControl: '300' });
  if (error) throw error;
  const { data } = supabase.storage.from('org-logos').getPublicUrl(path);
  return `${data.publicUrl}?v=${Date.now()}`;
};

// ─── Administración de la plataforma ─────────────────────────────────────────
export const platformListOrgs = async () => {
  const { data, error } = await supabase.rpc('platform_list_organizations');
  if (error) throw error;
  return data || [];
};
export const platformGetSettings = async () => {
  const { data, error } = await supabase.rpc('platform_get_settings');
  if (error) throw error;
  return data || {};
};
export const platformSetSetting = async (key, value) => {
  const { error } = await supabase.rpc('platform_set_setting', { p_key: key, p_value: value });
  if (error) throw error;
};
export const platformListTrials = async () => {
  const { data, error } = await supabase.rpc('platform_list_trial_requests');
  if (error) throw error;
  return data || [];
};
export const platformSetTrial = async (id, status) => {
  const { error } = await supabase.rpc('platform_set_trial_request', { p_id: id, p_status: status });
  if (error) throw error;
};
export const setMyReplyTo = async (email) => {
  const { error } = await supabase.rpc('set_my_reply_to', { p_email: email || '' });
  if (error) throw error;
};
export const requestTrial = async ({ name, org, email, phone, plan, message }) => {
  const { error } = await supabase.rpc('request_trial', {
    p_name: name, p_org: org, p_email: email, p_phone: phone || '', p_plan: plan || null, p_message: message || '',
  });
  if (error) throw error;
};
// type: 'test' | 'invite' (con employeeId). Devuelve true si salió.
export const sendOrgMail = async (type, employeeId) => {
  const { data, error } = await supabase.functions.invoke('send-email', { body: { type, employee_id: employeeId } });
  if (error) {
    let msg = error.message;
    try { const j = await error.context?.json?.(); if (j?.error) msg = j.error; } catch (_) { /* sin detalle */ }
    throw new Error(msg || 'No se pudo enviar');
  }
  if (data?.error) throw new Error(data.error);
  return true;
};
export const getOrgPublicBrand = async (slug) => {
  const { data } = await supabase.rpc('org_public_brand', { p_slug: slug });
  return data || null;
};
export const welcomeOwner = async (orgId) => {
  const { data, error } = await supabase.functions.invoke('send-email', { body: { type: 'owner_welcome', org_id: orgId } });
  if (error) {
    let msg = error.message;
    try { const j = await error.context?.json?.(); if (j?.error) msg = j.error; } catch (_) { /* sin detalle */ }
    throw new Error(msg || 'No se pudo enviar');
  }
  if (data?.error) throw new Error(data.error);
  return true;
};
export const getPublicPlans = async () => {
  const { data } = await supabase.rpc('public_plans');
  return data || [];
};
export const platformSetPlanPrice = async (plan, amount, currency, note) => {
  const { error } = await supabase.rpc('platform_set_plan_price', { p_plan: plan, p_amount: amount, p_currency: currency, p_note: note || '' });
  if (error) throw error;
};
export const getLegalInfo = async () => {
  const { data } = await supabase.rpc('public_legal_info');
  return data || {};
};
export const getMyConsents = async () => {
  const { data, error } = await supabase.rpc('my_consents');
  if (error) throw error;
  return data || [];
};
export const acceptConsent = async (doc, version) => {
  const { error } = await supabase.rpc('accept_consent', { p_doc: doc, p_version: version });
  if (error) throw error;
};
export const platformOrgUsage = async () => {
  const { data, error } = await supabase.rpc('platform_org_usage');
  if (error) throw error;
  return data || [];
};
export const platformCreateOrg = async ({ name, plan, ownerName, ownerEmail, ownerPassword, trialDays }) => {
  const { data, error } = await supabase.rpc('platform_create_organization', {
    p_name: name, p_plan: plan, p_owner_name: ownerName, p_owner_email: ownerEmail,
    p_owner_password: ownerPassword, p_trial_days: trialDays,
  });
  if (error) throw error;
  return data;
};
export const platformSetStatus = async (orgId, status) => {
  const { error } = await supabase.rpc('platform_set_org_status', { p_org: orgId, p_status: status });
  if (error) throw error;
};
export const platformSetPlan = async (orgId, plan, override) => {
  const { error } = await supabase.rpc('platform_set_org_plan', { p_org: orgId, p_plan: plan, p_max_employees_override: override });
  if (error) throw error;
};
export const platformSetOwnerPassword = async (orgId, password) => {
  const { error } = await supabase.rpc('platform_set_owner_password', { p_org: orgId, p_password: password });
  if (error) throw error;
};
export const platformAudit = async (limit = 100) => {
  const { data, error } = await supabase.rpc('platform_audit', { p_limit: limit });
  if (error) throw error;
  return data || [];
};

// Respaldo completo (JSON) de la organización
export const exportMyOrg = async () => {
  const { data, error } = await supabase.rpc('export_my_org');
  if (error) throw error;
  return data;
};
export const platformExportOrg = async (orgId) => {
  const { data, error } = await supabase.rpc('platform_export_org', { p_org: orgId });
  if (error) throw error;
  return data;
};
export const downloadJson = (obj, name) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url; a.download = `${name}-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

// ── Planes configurables (plataforma) ──
export const platformPlans = async () => {
  const { data, error } = await supabase.rpc('platform_plans');
  if (error) throw error;
  return data || [];
};
export const platformUpsertPlan = async (p) => {
  const { error } = await supabase.rpc('platform_upsert_plan', {
    p_id: p.id, p_name: p.name, p_description: p.description || '', p_who: p.who || '', p_hot: !!p.hot, p_public: p.is_public !== false, p_archived: !!p.archived,
    p_max_employees: p.max_employees === '' || p.max_employees == null ? null : Number(p.max_employees),
    p_max_locations: p.max_locations === '' || p.max_locations == null ? null : Number(p.max_locations),
    p_features: p.features || {}, p_sort: Number(p.sort_order) || 100,
    p_price: p.price_amount === '' || p.price_amount == null ? null : Number(p.price_amount), p_currency: p.price_currency || 'ARS', p_note: p.price_note || '',
  });
  if (error) throw error;
};
export const platformSetOrgEntitlements = async (orgId, plan, maxEmployees, maxLocations, overrides) => {
  const { error } = await supabase.rpc('platform_set_org_entitlements', {
    p_org: orgId, p_plan: plan, p_max_employees: maxEmployees || null, p_max_locations: maxLocations || null, p_overrides: overrides || {},
  });
  if (error) throw error;
};
export const platformOrgEntitlements = async () => {
  const { data, error } = await supabase.rpc('platform_org_entitlements');
  if (error) throw error;
  return data || {};
};

// ── Gestión completa de cada organización (plataforma) ──
export const platformOrgDetails = async (orgId) => {
  const { data, error } = await supabase.rpc('platform_org_details', { p_org: orgId });
  if (error) throw error;
  return data;
};
export const platformUpdateOrg = async (orgId, v) => {
  const { error } = await supabase.rpc('platform_update_org', {
    p_org: orgId, p_name: v.name, p_slug: v.slug, p_timezone: v.timezone || null,
    p_sender_name: v.sender_name || null, p_reply_to: v.reply_to || null,
    p_trial_ends_at: v.trial_ends_at || null, p_notes: v.notes || null,
  });
  if (error) throw error;
};
export const platformOrgPeople = async (orgId) => {
  const { data, error } = await supabase.rpc('platform_org_people', { p_org: orgId });
  if (error) throw error;
  return data || [];
};
export const platformUpdatePerson = async (userId, { name, email, active }) => {
  const { error } = await supabase.rpc('platform_update_person', { p_user: userId, p_name: name, p_email: email, p_active: active });
  if (error) throw error;
};
export const platformSetPersonPassword = async (userId, password) => {
  const { error } = await supabase.rpc('platform_set_person_password', { p_user: userId, p_password: password });
  if (error) throw error;
};
export const adminSetUserEmail = async (userId, email) => {
  const { error } = await supabase.rpc('admin_set_user_email', { p_user: userId, p_email: email });
  if (error) throw error;
};
export const platformCreatePerson = async (orgId, { name, email, password, level }) => {
  const { data, error } = await supabase.rpc('platform_create_person', { p_org: orgId, p_name: name, p_email: email, p_password: password, p_level: level });
  if (error) throw error;
  return data;
};
export const platformSetPersonRole = async (userId, level) => {
  const { error } = await supabase.rpc('platform_set_person_role', { p_user: userId, p_level: level });
  if (error) throw error;
};

// Envía un comprobante (HTML breve) al mail de la persona. Requiere permiso de sueldos.
export const sendReceiptMail = async (employeeId, subject, html) => {
  const { data, error } = await supabase.functions.invoke('send-email', { body: { type: 'receipt', employee_id: employeeId, subject, html } });
  if (error) {
    let msg = error.message;
    try { const j = await error.context?.json?.(); if (j?.error) msg = j.error; } catch (_) { /* sin detalle */ }
    throw new Error(msg || 'No se pudo enviar');
  }
  if (data?.error) throw new Error(data.error);
  return true;
};

// Dominio propio para los mails: action = 'status' | 'start' (domain, local) | 'verify' | 'remove'
export const mailDomain = async (action, extra = {}) => {
  const { data, error } = await supabase.functions.invoke('mail-domain', { body: { action, ...extra } });
  if (error) {
    let msg = error.message;
    try { const j = await error.context?.json?.(); if (j?.error) msg = j.error; } catch (_) { /* sin detalle */ }
    throw new Error(msg || 'No se pudo completar');
  }
  if (data?.error) throw new Error(data.error);
  return data;
};

export const setMyBrand = async ({ color, hideProduct }) => {
  const { error } = await supabase.rpc('set_my_brand', { p_color: color || null, p_hide_product: !!hideProduct });
  if (error) throw error;
};

export const getSignupInfo = async () => {
  const { data } = await supabase.rpc('public_signup_info');
  return data || { open: false, days: 14 };
};
// Alta directa (si Plataforma la tiene activada): crea la cuenta y manda el mail para elegir la clave
export const selfSignup = async ({ name, org, email, phone, plan, website }) => {
  const { data, error } = await supabase.functions.invoke('self-signup', { body: { name, org, email, phone, plan, website } });
  if (error) {
    let msg = error.message;
    try { const j = await error.context?.json?.(); if (j?.error) msg = j.error; } catch (_) { /* sin detalle */ }
    throw new Error(msg || 'No se pudo crear la cuenta');
  }
  if (data?.error) throw new Error(data.error);
  return data;
};
